import { useState } from "react"
import { router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDate } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Who is in the group, and who has been offered a place in it.
 *
 * **Reading and administering are different authorities.** Everyone who can
 * reach this screen sees the list — knowing who else is in your own group is not
 * privileged. Only a group `owner` gets the form and the revoke buttons, and
 * `canManageMembers` is the server's answer, not a guess made here. The controls
 * are absent rather than disabled: a disabled button invites the operator to
 * work out what would unlock it, and nothing here would.
 *
 * **A member row has no controls at all**, not even for an owner. Removing
 * someone from a group and changing their standing in it are decisions with
 * their own consequences for what they can read, and this section ships the one
 * thing that was missing — a way in. Taking someone out again is a separate
 * change, and shipping half of it as a button that does not exist yet would be
 * worse than the honest gap.
 *
 * An invitation row carries its deadline, because the question an owner has
 * about a pending invitation is always "has this gone stale".
 */

export type Member = {
  id: number
  name: string
  email: string
  role: string
  roleLabel: string
  isSelf: boolean
}

export type PendingInvite = {
  id: number
  email: string
  role: string
  roleLabel: string
  expiresAt: string
}

export type RoleOption = {
  value: string
  label: string
}

function tMembers(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.organization.show.members.${key}`, options)
}

function InviteForm({ roleOptions }: { roleOptions: RoleOption[] }) {
  const [email, setEmail] = useState("")
  const [role, setRole] = useState(roleOptions[0]?.value ?? "")

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      data-testid="organization-invite-create"
      onSubmit={(event) => {
        event.preventDefault()
        router.post(
          "/organization_invites",
          { email, role },
          { preserveScroll: true, onSuccess: () => setEmail("") }
        )
      }}
    >
      <div className="flex min-w-48 flex-1 flex-col gap-1">
        <Label htmlFor="organization-invite-email" className="text-muted-foreground text-xs">
          {tMembers("email_label")}
        </Label>
        <Input
          id="organization-invite-email"
          type="email"
          required
          value={email}
          placeholder={tMembers("email_placeholder")}
          data-testid="organization-invite-email"
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="flex min-w-36 flex-col gap-1">
        <Label htmlFor="organization-invite-role" className="text-muted-foreground text-xs">
          {tMembers("role_label")}
        </Label>
        {/* The kit's Select, not a native one. A bare `<select>` inherits the
            theme's foreground colour while the browser paints its own white
            background, which is white-on-white in dark mode — and it cannot
            carry the per-option testId the Playwright suite selects by. */}
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger
            id="organization-invite-role"
            className="w-full"
            data-testid="organization-invite-role"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roleOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`organization-invite-role-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        className="h-11 md:h-9"
        disabled={email.trim() === ""}
        data-testid="organization-invite-submit"
      >
        {tMembers("invite")}
      </Button>
    </form>
  )
}

export function GroupMembers({
  members,
  invites,
  roleOptions,
  canManageMembers,
  timeZone,
}: {
  members: Member[]
  invites: PendingInvite[]
  roleOptions: RoleOption[]
  canManageMembers: boolean
  timeZone: string
}) {
  return (
    <Card density="compact" data-testid="organization-members-card">
      <CardHeader className="border-b">
        <CardTitle>{tMembers("title")}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <ul className="divide-border divide-y rounded-md border" data-testid="organization-member-list">
          {members.map((member) => (
            <li
              key={member.id}
              className="flex flex-wrap items-center gap-3 px-4 py-3"
              data-testid={`organization-member-${member.id}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {member.name}
                  {member.isSelf ? ` · ${tMembers("you")}` : ""}
                </span>
                <span className="text-muted-foreground block truncate text-xs">{member.email}</span>
              </span>

              <span className="text-muted-foreground text-xs">{member.roleLabel}</span>
            </li>
          ))}
        </ul>

        {invites.length > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground text-xs">{tMembers("pending_title")}</p>

            <ul className="divide-border divide-y rounded-md border" data-testid="organization-invite-list">
              {invites.map((invite) => (
                <li
                  key={invite.id}
                  className="flex flex-wrap items-center gap-3 px-4 py-3"
                  data-testid={`organization-invite-${invite.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{invite.email}</span>
                    <span className="text-muted-foreground block truncate text-xs tabular-nums">
                      {tMembers("expires_on", {
                        date: formatDate(invite.expiresAt, { timeZone }),
                      })}
                    </span>
                  </span>

                  <span className="text-muted-foreground text-xs">{invite.roleLabel}</span>

                  {canManageMembers && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={tMembers("revoke", { email: invite.email })}
                      data-testid={`organization-invite-revoke-${invite.id}`}
                      onClick={() =>
                        router.delete(`/organization_invites/${invite.id}`, { preserveScroll: true })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {canManageMembers && <InviteForm roleOptions={roleOptions} />}
      </CardContent>
    </Card>
  )
}
